import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpIconTooltip } from '@/components/shared/HelpIconTooltip';
import type { SupplierPerformance } from '@/services/orderTracking/orderTracking.types';

/** Máximo de tarjetas: prioriza riesgo, sin volcar el listado completo. */
const MAX_VISIBLE_CARDS = 8;

interface SupplierPerformancePanelProps {
  suppliers: SupplierPerformance[];
  selectedSuppliers: string[];
  onToggleSupplier: (supplier: string) => void;
}

function riskBadgeClasses(level: SupplierPerformance['riskLevel']): string {
  if (level === 'critical') return 'bg-red-100 text-red-800 border-red-200';
  if (level === 'warning') return 'bg-amber-50 text-amber-800 border-amber-200';
  return 'bg-green-50 text-green-700 border-green-200';
}

function complianceColor(value: number): string {
  if (value >= 70) return 'text-green-700';
  if (value >= 50) return 'text-amber-700';
  return 'text-red-700';
}

function sortByRisk(a: SupplierPerformance, b: SupplierPerformance): number {
  const riskOrder = { critical: 0, warning: 1, ok: 2 };
  const ra = riskOrder[a.riskLevel ?? 'ok'];
  const rb = riskOrder[b.riskLevel ?? 'ok'];
  if (ra !== rb) return ra - rb;
  if (b.criticalOrders !== a.criticalOrders) return b.criticalOrders - a.criticalOrders;
  return a.leadTimeCompliance - b.leadTimeCompliance;
}

export function SupplierPerformancePanel({
  suppliers,
  selectedSuppliers,
  onToggleSupplier,
}: SupplierPerformancePanelProps) {
  const { t } = useTranslation();

  const sortedSuppliers = useMemo(() => [...suppliers].sort(sortByRisk), [suppliers]);

  const attentionSuppliers = useMemo(
    () => sortedSuppliers.filter((s) => (s.riskLevel ?? 'ok') !== 'ok'),
    [sortedSuppliers]
  );

  const visibleSuppliers = useMemo(() => {
    const byName = new Map(sortedSuppliers.map((s) => [s.supplier, s]));
    // Siempre mostrar los filtrados (aunque no estén en el top 8).
    const pinned = selectedSuppliers
      .map((name) => byName.get(name))
      .filter((s): s is SupplierPerformance => Boolean(s));
    const pinnedNames = new Set(pinned.map((s) => s.supplier));

    const pool =
      attentionSuppliers.length > 0
        ? attentionSuppliers
        : sortedSuppliers.slice(0, Math.min(4, sortedSuppliers.length));
    const rest = pool.filter((s) => !pinnedNames.has(s.supplier));

    if (pinned.length === 0) {
      return pool.slice(0, MAX_VISIBLE_CARDS);
    }
    const slotsForRest = Math.max(MAX_VISIBLE_CARDS - pinned.length, 0);
    return [...pinned, ...rest.slice(0, slotsForRest)];
  }, [attentionSuppliers, selectedSuppliers, sortedSuppliers]);

  const criticalCount = useMemo(
    () => sortedSuppliers.filter((s) => s.riskLevel === 'critical').length,
    [sortedSuppliers]
  );
  const warningCount = useMemo(
    () => sortedSuppliers.filter((s) => s.riskLevel === 'warning').length,
    [sortedSuppliers]
  );
  const hiddenCount = Math.max(sortedSuppliers.length - visibleSuppliers.length, 0);

  if (suppliers.length === 0) {
    return null;
  }

  const renderCard = (supplier: SupplierPerformance) => {
    const isSelected = selectedSuppliers.includes(supplier.supplier);
    const riskLevel = supplier.riskLevel ?? 'ok';
    const histN = supplier.historicalOrdersEvaluated ?? 0;
    const hasHistory = histN > 0;

    return (
      <button
        key={supplier.supplier}
        type="button"
        onClick={() => onToggleSupplier(supplier.supplier)}
        className={`text-left rounded-lg border p-3 transition-colors hover:border-blue-300 hover:bg-blue-50/40 ${
          isSelected ? 'border-blue-400 bg-blue-50 ring-1 ring-blue-200' : 'border-gray-200 bg-white'
        }`}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <p className="text-sm font-semibold text-gray-900 truncate" title={supplier.supplier}>
            {supplier.supplier}
          </p>
          <span
            className={`shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${riskBadgeClasses(riskLevel)}`}
          >
            {t(`orderTracking.suppliers.risk.${riskLevel}`)}
          </span>
        </div>
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-600">
          <span>
            {supplier.orders} {t('orderTracking.suppliers.ordersShort')}
          </span>
          {supplier.criticalOrders > 0 && (
            <span className="text-red-700 font-medium">
              {supplier.criticalOrders} {t('orderTracking.suppliers.criticalShort')}
            </span>
          )}
          {hasHistory ? (
            <span className={`font-medium ${complianceColor(supplier.leadTimeCompliance)}`}>
              {supplier.leadTimeCompliance}% {t('orderTracking.suppliers.complianceShort')}
            </span>
          ) : (
            <span className="text-gray-400" title={t('orderTracking.suppliers.noHistoryTooltip')}>
              {t('orderTracking.suppliers.noHistoryShort')}
            </span>
          )}
        </div>
      </button>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 min-w-0 max-w-full">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold text-gray-900">{t('orderTracking.suppliers.title')}</h2>
          <HelpIconTooltip text={t('orderTracking.suppliers.titleTooltip')} />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
          {(criticalCount > 0 || warningCount > 0) && (
            <span className="inline-flex items-center gap-1.5">
              {criticalCount > 0 && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded border bg-red-50 text-red-800 border-red-200">
                  {t('orderTracking.suppliers.summaryCritical', { count: criticalCount })}
                </span>
              )}
              {warningCount > 0 && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-200">
                  {t('orderTracking.suppliers.summaryWarning', { count: warningCount })}
                </span>
              )}
            </span>
          )}
          <span>{t('orderTracking.suppliers.count', { count: suppliers.length })}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        {visibleSuppliers.map(renderCard)}
      </div>

      {hiddenCount > 0 && selectedSuppliers.length === 0 && (
        <p className="mt-3 text-xs text-gray-500">
          {t('orderTracking.suppliers.showingTopHint', {
            shown: visibleSuppliers.length,
            hidden: hiddenCount,
          })}
        </p>
      )}

      {selectedSuppliers.length > 0 && (
        <p className="mt-2 text-xs text-blue-700">{t('orderTracking.suppliers.filterHint')}</p>
      )}
    </div>
  );
}
